import { Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

import { Trans, t } from '@lingui/macro';

export default function DiscountForm(props) {
    const { code, status, message, onSubmit, onChange, onDelete } = props;

    const handleCodeChange = (e) => {
        onChange(e.target.value.slice(0, 50));
    };

    const isSuccess = status === 'success';
    const isError = status === 'error';
    const isLoading = status === 'loading';
    const applyDisabled = isLoading || code.length === 0;

    const inputClassName = cn(
        'box-border h-11 w-full border border-solid py-0 text-sm font-inter leading-normal text-foreground shadow-none focus:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'border-[hsl(var(--border))] bg-[hsl(var(--input))]',
        isSuccess && 'border-primary/60 ring-1 ring-primary/25',
        isError && 'border-destructive focus:border-destructive',
    );

    const actionButtonClass =
        'h-11 w-full shrink-0 font-bold uppercase tracking-wide sm:mt-0 sm:w-[7.5rem]';

    return (
        <div className="mb-4 rounded-xl border border-solid border-border bg-card p-4 shadow-none">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                    <div className="min-w-0 flex-1">
                        <label
                            htmlFor="discount-code"
                            className={cn(
                                'mb-1.5 block text-sm font-medium',
                                isSuccess ? 'text-primary' : 'text-foreground',
                            )}
                        >
                            <Trans>Discount Code</Trans>
                        </label>
                        <Input
                            id="discount-code"
                            type="text"
                            autoComplete="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            value={code}
                            readOnly={isSuccess}
                            disabled={isLoading}
                            onChange={handleCodeChange}
                            className={inputClassName}
                            aria-invalid={isError}
                            aria-describedby={message ? 'discount-code-helper' : undefined}
                        />
                    </div>

                    {isSuccess ? (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onDelete}
                            className={cn(
                                actionButtonClass,
                                'border-destructive text-destructive hover:bg-destructive/10',
                            )}
                        >
                            <Trans>Remove</Trans>
                        </Button>
                    ) : isLoading ? (
                        <Button
                            type="button"
                            variant="default"
                            disabled
                            className={actionButtonClass}
                            aria-busy
                            aria-label={t`Applying discount`}
                        >
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            <Trans>Apply</Trans>
                        </Button>
                    ) : applyDisabled ? (
                        <Button
                            type="button"
                            variant="secondary"
                            disabled
                            className={actionButtonClass}
                        >
                            <Trans>Apply</Trans>
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="default"
                            onClick={onSubmit}
                            className={actionButtonClass}
                        >
                            <Trans>Apply</Trans>
                        </Button>
                    )}
                </div>

                {message ? (
                    <p
                        id="discount-code-helper"
                        className={cn(
                            'text-sm',
                            isError && 'text-destructive',
                            isSuccess && 'text-primary',
                            !isError && !isSuccess && 'text-muted-foreground',
                        )}
                    >
                        {message}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
